/**
 * Strategy Canvas Store
 *
 * Manages Strategy Canvas documents per workspace.
 * Each canvas holds six editable text components (vision, purpose, mission,
 * guiding_principles, competencies, assumptions) plus a title.
 * Canvases can be linked to Strategy objects via the
 * strategy_canvas_strategies join table.
 */

import { create } from 'zustand';
import { getSafeSupabaseClient } from '../../lib/supabaseSync';
import { getCurrentWorkspaceId, generateUUID } from '../../lib/supabaseSync';

// ── Types ─────────────────────────────────────────────────────

export interface StrategyCanvas {
  id: string;
  workspaceId: string;
  title: string;
  vision: string | null;
  purpose: string | null;
  mission: string | null;
  guidingPrinciples: string | null;
  competencies: string | null;
  assumptions: string | null;
  linkedStrategyIds: string[];
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCanvasInput {
  title?: string;
}

export interface UpdateCanvasInput {
  title?: string;
  vision?: string | null;
  purpose?: string | null;
  mission?: string | null;
  guidingPrinciples?: string | null;
  competencies?: string | null;
  assumptions?: string | null;
}

// ── DB row mapper ─────────────────────────────────────────────

function rowToCanvas(
  row: Record<string, unknown>,
  linkedStrategyIds: string[] = []
): StrategyCanvas {
  return {
    id:                row.id as string,
    workspaceId:       row.workspace_id as string,
    title:             (row.title as string) ?? 'Untitled Canvas',
    vision:            (row.vision as string) ?? null,
    purpose:           (row.purpose as string) ?? null,
    mission:           (row.mission as string) ?? null,
    guidingPrinciples: (row.guiding_principles as string) ?? null,
    competencies:      (row.competencies as string) ?? null,
    assumptions:       (row.assumptions as string) ?? null,
    linkedStrategyIds,
    createdBy:         (row.created_by as string) ?? null,
    createdAt:         row.created_at as string,
    updatedAt:         row.updated_at as string,
  };
}

// ── Store interface ───────────────────────────────────────────

interface StrategyCanvasState {
  canvases: StrategyCanvas[];
  isLoading: boolean;
  syncError: string | null;

  fetchCanvases: () => Promise<void>;
  createCanvas: (input?: CreateCanvasInput) => Promise<{ canvas: StrategyCanvas | null; error?: string }>;
  updateCanvas: (id: string, updates: UpdateCanvasInput) => Promise<{ success: boolean; error?: string }>;
  deleteCanvas: (id: string) => Promise<{ success: boolean; error?: string }>;

  /** Link a strategy to a canvas */
  linkStrategy: (canvasId: string, strategyId: string) => Promise<{ success: boolean; error?: string }>;
  /** Unlink a strategy from a canvas */
  unlinkStrategy: (canvasId: string, strategyId: string) => Promise<{ success: boolean; error?: string }>;
}

// ── Store ─────────────────────────────────────────────────────

export const useStrategyCanvasStore = create<StrategyCanvasState>((set, get) => ({
  canvases:  [],
  isLoading: false,
  syncError: null,

  fetchCanvases: async () => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return;

    set({ isLoading: true, syncError: null });
    try {
      const [canvasRes, linksRes] = await Promise.all([
        supabase
          .from('strategy_canvases')
          .select('*')
          .eq('workspace_id', workspaceId)
          .order('created_at', { ascending: false }),
        supabase
          .from('strategy_canvas_strategies')
          .select('canvas_id, strategy_id')
          .eq('workspace_id', workspaceId),
      ]);

      if (canvasRes.error) {
        console.error('[StrategyCanvasStore] fetch error:', canvasRes.error);
        set({ isLoading: false, syncError: canvasRes.error.message });
        return;
      }

      // Build a canvas_id → strategy_ids[] map
      const linkMap = new Map<string, string[]>();
      for (const link of (linksRes.data ?? [])) {
        const row = link as { canvas_id: string; strategy_id: string };
        if (!linkMap.has(row.canvas_id)) linkMap.set(row.canvas_id, []);
        linkMap.get(row.canvas_id)!.push(row.strategy_id);
      }

      const canvases = (canvasRes.data ?? []).map((row) =>
        rowToCanvas(row as Record<string, unknown>, linkMap.get((row as Record<string, unknown>).id as string) ?? [])
      );

      set({ canvases, isLoading: false });
    } catch (err) {
      console.error('[StrategyCanvasStore] fetch exception:', err);
      set({ isLoading: false, syncError: 'Failed to load canvases' });
    }
  },

  createCanvas: async (input = {}) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { canvas: null, error: 'No Supabase client or workspace' };

    const { data, error } = await supabase
      .from('strategy_canvases')
      .insert({
        id:           generateUUID(),
        workspace_id: workspaceId,
        title:        input.title?.trim() || 'Untitled Canvas',
      })
      .select('*')
      .single();

    if (error) {
      console.error('[StrategyCanvasStore] createCanvas error:', error);
      return { canvas: null, error: error.message };
    }

    const canvas = rowToCanvas(data as Record<string, unknown>, []);
    set((state) => ({ canvases: [canvas, ...state.canvases] }));
    return { canvas };
  },

  updateCanvas: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const dbUpdates: Record<string, unknown> = {};
    if (updates.title             !== undefined) dbUpdates.title              = updates.title;
    if (updates.vision            !== undefined) dbUpdates.vision             = updates.vision;
    if (updates.purpose           !== undefined) dbUpdates.purpose            = updates.purpose;
    if (updates.mission           !== undefined) dbUpdates.mission            = updates.mission;
    if (updates.guidingPrinciples !== undefined) dbUpdates.guiding_principles = updates.guidingPrinciples;
    if (updates.competencies      !== undefined) dbUpdates.competencies       = updates.competencies;
    if (updates.assumptions       !== undefined) dbUpdates.assumptions        = updates.assumptions;

    // Optimistic update
    set((state) => ({
      canvases: state.canvases.map((c) =>
        c.id === id ? { ...c, ...updates } : c
      ),
    }));

    const { error } = await supabase
      .from('strategy_canvases')
      .update(dbUpdates)
      .eq('id', id);

    if (error) {
      console.error('[StrategyCanvasStore] updateCanvas error:', error);
      get().fetchCanvases();
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  deleteCanvas: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    set((state) => ({ canvases: state.canvases.filter((c) => c.id !== id) }));

    const { error } = await supabase
      .from('strategy_canvases')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('[StrategyCanvasStore] deleteCanvas error:', error);
      get().fetchCanvases();
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  linkStrategy: async (canvasId, strategyId) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { success: false, error: 'No Supabase client or workspace' };

    // Optimistic update
    set((state) => ({
      canvases: state.canvases.map((c) =>
        c.id === canvasId && !c.linkedStrategyIds.includes(strategyId)
          ? { ...c, linkedStrategyIds: [...c.linkedStrategyIds, strategyId] }
          : c
      ),
    }));

    const { error } = await supabase
      .from('strategy_canvas_strategies')
      .insert({ canvas_id: canvasId, strategy_id: strategyId, workspace_id: workspaceId });

    if (error) {
      console.error('[StrategyCanvasStore] linkStrategy error:', error);
      get().fetchCanvases();
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  unlinkStrategy: async (canvasId, strategyId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    // Optimistic update
    set((state) => ({
      canvases: state.canvases.map((c) =>
        c.id === canvasId
          ? { ...c, linkedStrategyIds: c.linkedStrategyIds.filter((id) => id !== strategyId) }
          : c
      ),
    }));

    const { error } = await supabase
      .from('strategy_canvas_strategies')
      .delete()
      .eq('canvas_id', canvasId)
      .eq('strategy_id', strategyId);

    if (error) {
      console.error('[StrategyCanvasStore] unlinkStrategy error:', error);
      get().fetchCanvases();
      return { success: false, error: error.message };
    }
    return { success: true };
  },
}));
